/**
 * Renders a multi-line text with auto-detected http/https URLs as clickable
 * links. Linkifies *only* well-formed URLs (must start with http:// or
 * https://) so we don't accidentally turn "art. 5º" into a link. URLs that
 * point to YouTube watch/be/shorts/playlist are also accepted; any other
 * external URL opens in a new tab with rel="noopener noreferrer".
 *
 * Usage:
 *   <LinkifiedText text={episode.description} />
 *
 * Pure-text fallback when text is empty: returns null.
 */

const URL_REGEX = /(https?:\/\/[^\s<>"']+[^\s<>"',.;:!?()])/gi;

function isYouTube(url) {
  try {
    const u = new URL(url);
    return /(^|\.)youtube\.com$/i.test(u.hostname) || /(^|\.)youtu\.be$/i.test(u.hostname);
  } catch {
    return false;
  }
}

function shortLabel(url) {
  try {
    const u = new URL(url);
    const path = u.pathname.length > 1 ? u.pathname : "";
    const compact = `${u.hostname.replace(/^www\./, "")}${path}`;
    return compact.length > 60 ? compact.slice(0, 57) + "…" : compact;
  } catch {
    return url;
  }
}

export default function LinkifiedText({ text, className = "", testid = "linkified-text" }) {
  if (!text) return null;
  const parts = String(text).split(URL_REGEX);

  return (
    <div className={`whitespace-pre-wrap break-words ${className}`} data-testid={testid}>
      {parts.map((p, i) => {
        if (i % 2 === 1) {
          // odd indices are the matched URLs
          const yt = isYouTube(p);
          return (
            <a
              key={`url-${i}-${p.slice(0, 16)}`}
              href={p}
              target="_blank"
              rel="noopener noreferrer"
              className={`underline underline-offset-2 break-all ${yt ? "text-red-300 hover:text-red-200" : "text-sky-300 hover:text-sky-200"}`}
              data-testid={`linkified-url-${i}`}
              title={p}
            >
              {shortLabel(p)}
            </a>
          );
        }
        return <span key={`txt-${i}-${p.length}`}>{p}</span>;
      })}
    </div>
  );
}
